import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';

import { Logger } from '@futrli/shared-services';
import { ExportedReportItem } from '@futrli-sdk/report_scheduling';
import { UILoader, toastError } from '@futrli/ui-components';

import { Data, TABLE_COLUMN } from '../../../../utils/table-filters/filter-table-types';
import { Filter } from '../../../../utils/table-filters/components/filter';
import { getExportedReports } from '../../../../core/api/reporting/report-document-export';
import { initialFilterTableState } from '../../../../utils/__mocks__/table-filters';
import { useMembersContext } from '../../../members-context';
import { useLocalStorageTableFilters } from '../../../../utils/table-filters/hooks/use-local-storage-table-filters/use-local-storage-table-filters';
import { useTableFilters } from '../../../../utils/table-filters/hooks/use-table-filters/use-table-filters';
import { VirtualizedList, VIRTUALIZED_LIST_TYPE } from '../../../../ui/virtualized-list/virtualized-list';

import * as styles from '../document-library.scss';
import { usePredictionsInfoContext } from '../../../../forecast/detail/context/predictions-info-context';

export interface NormalizedExportedReportItem extends ExportedReportItem {
  saved_on: Date;
}

export enum EXPORTED_PDF_STATUS {
  PENDING_RENDERING = 'pending-rendering',
  RENDERED = 'rendered',
  FAILED_RENDERING = 'failed-rendering'
}

export const options = [
  { id: EXPORTED_PDF_STATUS.PENDING_RENDERING, name: 'Pending rendering' },
  { id: EXPORTED_PDF_STATUS.RENDERED, name: 'Rendered' },
  { id: EXPORTED_PDF_STATUS.FAILED_RENDERING, name: 'Failed rendering' }
];

const LOCAL_STORAGE_KEY = 'exported-pdfs-filters';

const columns = [
  {
    display_name: <FormattedMessage id="reporting.pdf_name" defaultMessage="PDF name" />,
    name: TABLE_COLUMN.REPORT_NAME,
    classes: clsx(styles.flex6)
  },
  {
    display_name: <FormattedMessage id="common.status" defaultMessage="Status" />,
    name: TABLE_COLUMN.STATUS,
    classes: clsx(styles.flex2),
    hideHeadCell: true
  },
  {
    display_name: <FormattedMessage id="reporting.created_by" defaultMessage="Created by" />,
    name: TABLE_COLUMN.CREATED_BY,
    classes: clsx(styles.flex2)
  },
  {
    display_name: <FormattedMessage id="reporting.saved_on" defaultMessage="Saved on" />,
    name: TABLE_COLUMN.SAVED_ON,
    classes: clsx(styles.flex2)
  }
];

const filterColumns = [TABLE_COLUMN.SAVED_ON];

const MIN_QUERY_LENGTH = 3;

// const REFRESH_INTERVAL = 2000;

export const ExportedPdfs = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<NormalizedExportedReportItem[]>();
  const { isConsolidation } = usePredictionsInfoContext();

  // const [startPooling, setStartPooling] = useState(false);

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();

  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';

  const { isLoading: isMembersLoading, members } = useMembersContext();

  const {
    state: filterTableState,
    setInitialData,
    setInitialFiltersAndSort,
    handleCloseFilter,
    handleOpenFilter,
    handleClearColumnFilter,
    handleSortClick,
    handleSubmit
  } = useTableFilters(initialFilterTableState);

  useLocalStorageTableFilters(
    LOCAL_STORAGE_KEY,
    organisationId,
    filterTableState,
    filterColumns,
    setInitialFiltersAndSort
  );

  const { filterAnchorElement, activeColumn, sortByColumn, filters, sort, displayedData } = filterTableState;

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const fetchedExportedPdfs = await getExportedReports(workspaceId, organisationId, isConsolidation);
      // const existsPendingReport = !!fetchedExportedPdfs.find(r => r.status === 'pending-rendering');
      // if (existsPendingReport) {
      //   setStartPooling(true);
      // }
      const normalizedData = fetchedExportedPdfs.map(d => {
        const member = members.find(m => m.id === d.created_by);
        return {
          ...d,
          saved_on: new Date(d.created),
          created_by: member ? `${member.name.first_name} ${member.name.last_name}` : ''
        };
      });
      return normalizedData;
    } catch (e) {
      Logger.error(e as string);
      toastError();
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, organisationId, isConsolidation, members]);

  const fetchInitialData = useCallback(async () => {
    const initialData = await fetchData();
    setData(initialData);
    setInitialData((initialData as unknown) as Data[]);
  }, [fetchData, setInitialData]);

  // Fetch initial data
  useEffect(() => {
    if (members.length > 0) {
      fetchInitialData();
    }
  }, [fetchInitialData, members.length]);

  // Periodically check for new data if there is any reports in pending-rendering status
  // useEffect(() => {
  //   let id: any; // eslint-disable-line @typescript-eslint/no-explicit-any

  //   const periodicallyFetchData = async () => {
  //     try {
  //       const fetchedExportedPdfs = await getExportedReports(workspaceId, organisationId);
  //       const noPendingReports = fetchedExportedPdfs.filter(r => r.status === 'pending-rendering').length === 0;
  //       const normalizedData = fetchedExportedPdfs.map(d => {
  //         const member = members.find(m => m.id === d.created_by);
  //         return {
  //           ...d,
  //           saved_on: new Date(d.created),
  //           created_by: member ? `${member.name.first_name} ${member.name.last_name}` : ''
  //         };
  //       });
  //       // setData(normalizedData);
  //       if (noPendingReports) {
  //         clearInterval(id);
  //       }
  //     } catch (e) {
  //       Logger.error('Error while pooling');
  //       Logger.error(e as string);
  //     }
  //   };

  //   if (startPooling) {
  //     id = setInterval(() => {
  //       periodicallyFetchData();
  //     }, REFRESH_INTERVAL);
  //     periodicallyFetchData();
  //   }

  //   return () => {
  //     id && clearInterval(id);
  //   };
  // }, [startPooling, workspaceId, organisationId, members]);

  const onClearColumnFilter = useCallback(() => handleClearColumnFilter(initialFilterTableState.filters), [
    handleClearColumnFilter
  ]);

  if (isLoading || isMembersLoading) {
    return (
      <div>
        <UILoader isFull />
      </div>
    );
  }

  return (
    <div>
      <h1 className={styles.title} data-testid="exportedPdfsContent">
        <FormattedMessage id="reporting.exported_pdfs" defaultMessage="Exported PDFs" />
      </h1>
      <VirtualizedList
        data={displayedData}
        handleTableFilterClick={handleOpenFilter}
        filters={filters}
        sortByColumn={sortByColumn}
        type={VIRTUALIZED_LIST_TYPE.EXPORTED_PDFS}
        columns={columns}
        revalidateData={fetchInitialData}
      />
      <Filter
        data={(data as unknown) as Data[]}
        anchorElement={filterAnchorElement}
        handleSubmit={handleSubmit}
        handleClose={handleCloseFilter}
        handleClearColumnFilter={onClearColumnFilter}
        activeColumn={activeColumn}
        sortByColumn={sortByColumn}
        filters={filters}
        handleSortClick={handleSortClick}
        sort={sort}
        minQueryLength={MIN_QUERY_LENGTH}
        options={options}
      />
    </div>
  );
};
